import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
import type { TakiSdk } from '@taki/sdk';
import type { InstancesDeclarations } from './resolution.js';
import type { PortDeBoutonsFlux } from './boutons-flux.js';

/**
 * CE QUE L'HOTE FOURNIT AUX APPS DECLAREES : le SDK, et les declarations que
 * l'ORG a ecrites.
 *
 * POURQUOI C'EST L'HOTE QUI LE POSE, et pas le shell. `@taki/shell` n'a pas
 * `@taki/sdk` dans ses dependances, deliberement : il est le chrome (nav, auth,
 * hub), il ne parle pas aux donnees. C'est donc l'hote (`apps/hello`) qui
 * assemble, exactement comme il le fait deja pour les ports des modeles, des
 * liens, de la creation, de l'acces et du branding.
 *
 * LES INSTANCES SONT FACULTATIVES, et aujourd'hui absentes : le port Firestore
 * qui les lira arrive avec le chantier `app-studio-client`. Tant qu'elles ne
 * sont pas la, chaque app monte sa declaration NATIVE, ce qui est le
 * comportement d'avant ce chantier.
 */
export interface ValeurDuRuntime {
  sdk: TakiSdk;
  /**
   * L'org courante. Elle voyage jusqu'aux effets d'action (`ContexteDAppDeclaree`) :
   * le SDK la connait mais ne l'expose pas. Absente : `''`.
   */
  orgId?: string;
  /** Les declarations de l'org courante, par id d'app. Absentes : la native. */
  instances?: InstancesDeclarations;
  /**
   * LE COMPTE CONNECTE (son uid) et LE PROJET Firebase : ce que le poste garde
   * d'un contenu (brouillon, copie de secours, `sauvegarde/poste.ts`) est range
   * sous ces deux clefs ET l'org, pour qu'un navigateur partage ne rende jamais
   * le travail d'un compte a un autre. Absents : rien ne se garde sur le poste,
   * et l'ecran le dit.
   */
  compte?: string | null;
  projet?: string;
  /** La pile tourne sur les emulateurs locaux : les jeux de demo `local` s'y offrent. */
  emulateurs?: boolean;
  /**
   * LE PORT DES SMART BOUTONS FLUX (`boutons-flux.ts`). Absent : le port par
   * defaut, branche sur `sdk.flux` (`listerBranchementsDeBoutons`,
   * `declencherBouton`) ; un test ou un autre hote en pose un autre.
   */
  boutonsFlux?: PortDeBoutonsFlux;
}

/**
 * LE CONTEXTE, DEFAUT `null`, ET CE DEFAUT EST UN REFUS. Contrairement aux ports
 * du socle (une creation sans port se grise), un ecran declare SANS SDK n'a rien
 * a montrer du tout : il ne peut ni charger, ni resoudre une famille. Le dire au
 * montage, avec la phrase qui nomme le provider manquant, vaut mieux qu'un ecran
 * vide qu'on ira chercher dans les rules.
 */
const RuntimeContext = createContext<ValeurDuRuntime | null>(null);

/** Fournisseur pose par l'HOTE (celui qui a le SDK), autour du shell. */
export function AppRuntimeProvider({
  value,
  children,
}: {
  value: ValeurDuRuntime;
  children: ReactNode;
}): JSX.Element {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

/** Le SDK et les declarations de l'org. LEVE hors d'un `AppRuntimeProvider`. */
export function useAppRuntime(): ValeurDuRuntime {
  const valeur = useContext(RuntimeContext);
  if (valeur === null) {
    throw new Error(
      "Aucun AppRuntimeProvider au-dessus de cet ecran : une app declaree a besoin du SDK, " +
        "et c'est l'hote qui le fournit (apps/hello, App.tsx).",
    );
  }
  return valeur;
}
